import { Controller, useFormContext, type FieldPath } from 'react-hook-form';
import { PhoneCountryPicker } from '@ab-platform/ui/phone-country-picker';
import { FloatingLabelInput } from '@ab-platform/ui/floating-label-input';
import { FieldError } from '@ab-platform/ui/field-error';
import { type RegistrationFormValues } from '@/hooks/useRegistrationForm';

/**
 * One phone field = country prefix picker + local number input. Stores
 * a `{ prefixIso, number }` shape on the form so the user can change
 * country independently of the local part.
 *
 * `path` points at the OBJECT (e.g. "orgContacts.phone"), not the leaves
 * — the component appends `.prefixIso` / `.number` internally for the
 * `Controller` and `register()` bindings, and traverses
 * `formState.errors` to surface the per-field message.
 */
export interface PhoneFieldRowProps {
  /** Form path to the phone object. */
  path:
    | 'orgContacts.phone'
    | 'orgContacts.mobile'
    | 'adminUser.mobilePhone';
  /** Floating label on the number input (e.g. "Cellulare"). */
  label: string;
  /**
   * Optional autocomplete hint forwarded to the underlying input. The
   * picker has its own aria-label so we only pass this to the number
   * field.
   */
  autoComplete?: 'tel' | 'tel-national' | 'off';
}

export function PhoneFieldRow({
  path,
  label,
  autoComplete = 'tel',
}: PhoneFieldRowProps): JSX.Element {
  const form = useFormContext<RegistrationFormValues>();
  const { register, control, formState } = form;
  const numberPath = `${path}.number` as FieldPath<RegistrationFormValues>;
  const prefixPath = `${path}.prefixIso` as FieldPath<RegistrationFormValues>;
  const errors = readNested(formState.errors, path) as
    | { number?: { message?: string }; prefixIso?: { message?: string } }
    | undefined;

  return (
    <div className="grid gap-1.5">
      <div className="flex items-stretch gap-2">
        <Controller
          control={control}
          name={prefixPath}
          render={({ field }) => (
            <PhoneCountryPicker
              value={(field.value as string | undefined) ?? 'IT'}
              onChange={(iso2) => field.onChange(iso2)}
            />
          )}
        />
        <div className="min-w-0 flex-1">
          <FloatingLabelInput
            label={label}
            type="tel"
            inputMode="tel"
            autoComplete={autoComplete}
            error={errors?.number?.message}
            {...register(numberPath)}
          />
        </div>
      </div>
      <FieldError message={errors?.prefixIso?.message} />
    </div>
  );
}

function readNested(obj: unknown, path: string): unknown {
  let current: unknown = obj;
  for (const key of path.split('.')) {
    if (current === null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}
